import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { createAssociatedTokenAccountInstruction } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';
import { TransactionInstruction } from '@solana/web3.js';

import {
  connection,
  globalState,
  ORDER_OUTCOME,
  ORDER_TYPE,
  PREDICTION_SEEDS,
  program,
  token,
} from './solana';

import { retryWithDelay } from '@/lib/refetch';
import { bnToBuffer, mapOrderbook, toBN } from '@/lib/utils';
import { OrderbookState } from '@/types/contract';

export const MAX_SOLANA_PUBKEYS_PER_CALL = 100;

export const getPredictionStateAccount = (
  globalState: PublicKey,
  id: number,
) => {
  const [predictionState] = PublicKey.findProgramAddressSync(
    [
      Buffer.from(PREDICTION_SEEDS.PREDICTION_STATE_SEED),
      bnToBuffer(toBN(id), 'le', 8),
      globalState.toBuffer(),
    ],
    program.programId,
  );
  return predictionState;
};

export const getAdminState = () => {
  const [adminState] = PublicKey.findProgramAddressSync(
    [Buffer.from(PREDICTION_SEEDS.ADMIN_STATE_SEED), globalState.toBuffer()],
    program.programId,
  );
  return adminState;
};

export const getUserStateAccount = (user: PublicKey) => {
  const [userState] = PublicKey.findProgramAddressSync(
    [
      Buffer.from(PREDICTION_SEEDS.USER_STATE_SEED),
      user.toBuffer(),
      globalState.toBuffer(),
    ],
    program.programId,
  );
  return userState;
};

export const getReferralStateAccount = (code: string) => {
  const [referralState] = PublicKey.findProgramAddressSync(
    [
      Buffer.from(PREDICTION_SEEDS.REFERRAL_STATE_SEED),
      Buffer.from(code),
      globalState.toBuffer(),
    ],
    program.programId,
  );
  return referralState;
};

export const getUserPositionStateAccount = (
  user: PublicKey,
  marketId: number,
) => {
  const [userPositionState] = PublicKey.findProgramAddressSync(
    [
      Buffer.from(PREDICTION_SEEDS.USER_POSITION_STATE_SEED),
      bnToBuffer(toBN(marketId), 'le', 8),
      user.toBuffer(),
      globalState.toBuffer(),
    ],
    program.programId,
  );
  return userPositionState;
};

export const getVault = () => {
  const [vault] = PublicKey.findProgramAddressSync(
    [Buffer.from(PREDICTION_SEEDS.VAULT_SEED), globalState.toBuffer()],
    program.programId,
  );
  return vault;
};

export const getOrCreateATA = async (
  owner: PublicKey,
  payer?: PublicKey,
  inputToken: PublicKey = token,
): Promise<{
  ata: PublicKey;
  instruction: TransactionInstruction | null;
}> => {
  // Use the provided payer or default to owner
  const payerKey = payer || owner;

  // Get the ATA address
  const ata = getAssociatedTokenAddressSync(
    inputToken,
    owner,
    true, // Set to false when checking existing accounts
  );

  // Check if account exists
  const ataInfo = await retryWithDelay(() => connection.getAccountInfo(ata));

  // If account doesn't exist, create instruction to create it
  if (!ataInfo) {
    const createAtaIx = createAssociatedTokenAccountInstruction(
      payerKey, // payer
      ata, // ATA address
      owner, // owner
      inputToken, // mint
    );

    return {
      ata,
      instruction: createAtaIx,
    };
  }

  // Account exists, return only the address with null instruction
  return {
    ata,
    instruction: null,
  };
};

export const getMergeOrderId = (
  orderbook: OrderbookState,
  side: ORDER_TYPE,
  outcome: ORDER_OUTCOME,
  price: number,
  user: PublicKey,
) => {
  if (!orderbook) return null;
  const adjustedPrice = outcome === ORDER_OUTCOME.NO ? 100 - price : price;

  // Convert side/outcome to internal is_bid
  const isBid =
    (outcome === ORDER_OUTCOME.YES && side === ORDER_TYPE.BUY) ||
    (outcome === ORDER_OUTCOME.NO && side === ORDER_TYPE.SELL);

  // Get appropriate list
  const orders = isBid ? orderbook.bids : orderbook.asks;

  const order = orders.find(
    (order) =>
      order.price === adjustedPrice &&
      order.user.equals(user) &&
      +order.outcome === outcome &&
      order.isFilled === 0 &&
      order.expiryTimestamp >= 4_294_967_295 &&
      order.size > 0 &&
      order.expiryTimestamp > Date.now() / 1000,
  );

  if (order) return orders.indexOf(order);
  return null;
};

export const getPlaceLimitOrderPrevNodeId = (
  orderbook: OrderbookState,
  side: ORDER_TYPE,
  outcome: ORDER_OUTCOME,
  price: number,
) => {
  if (!orderbook) return null;
  // Convert to internal format - YES orders only
  const adjustedPrice = outcome === ORDER_OUTCOME.NO ? 100 - price : price;

  // Convert side/outcome to internal is_bid
  const isBid =
    (outcome === ORDER_OUTCOME.YES && side === ORDER_TYPE.BUY) ||
    (outcome === ORDER_OUTCOME.NO && side === ORDER_TYPE.SELL);

  // Get appropriate list
  const orders = isBid ? orderbook.bids : orderbook.asks;
  const head = isBid ? orderbook.bidHead : orderbook.askHead;
  // Special case: empty list
  if (head === 65535) return 65535;

  let current = head;
  let prev = 65535;

  // For bids: find first order with lower price
  // For asks: find first order with higher price
  while (current !== 65535) {
    const order = orders[current];

    if (order.size === 0 || order.expiryTimestamp < Date.now() / 1000) {
      current = order.next;
      continue;
    }

    if (isBid && order.price < adjustedPrice) break;
    if (!isBid && order.price > adjustedPrice) break;

    prev = current;
    current = order.next;
  }

  return prev;
};

export const getIdsToFill = (
  orderbook: OrderbookState,
  side: ORDER_TYPE,
  outcome: ORDER_OUTCOME,
  size: number,
) => {
  if (!orderbook) return null;

  // Determine whether to use bids or asks based on side and outcome
  // For YES outcome (0): BUY(0) checks asks, SELL(1) checks bids
  // For NO outcome (1): BUY(0) checks bids, SELL(1) checks asks
  const shouldCheckBids =
    outcome === ORDER_OUTCOME.YES
      ? side === ORDER_TYPE.SELL
      : side === ORDER_TYPE.BUY;

  const orders = shouldCheckBids ? orderbook.bids : orderbook.asks;

  const headId = shouldCheckBids ? orderbook.bidHead : orderbook.askHead;

  // List end is 0xffff (65535) as seen in the Rust code
  const LIST_END = 65535;

  const idsToFill: number[] = [];
  let remainingSize = size;
  let currentId = headId;

  // Traverse the linked lis  t starting from the head
  while (currentId !== LIST_END && remainingSize > 0) {
    const order = orders[currentId];
    // Only consider active, unfilled orders
    if (
      order &&
      order.size > 0 &&
      order.isFilled === 0 &&
      order.expiryTimestamp > Date.now() / 1000
    ) {
      idsToFill.push(currentId);
      remainingSize -= order.size;
    }

    // Move to the next order in the linked list
    currentId = order.next;
  }

  return idsToFill;
};

export const getAccountSchemeToSettle = async (
  orderBook: OrderbookState,
  type: ORDER_TYPE,
  marketId: number,
  ordersIds: number[] = [],
  limit = 5,
) => {
  if (!orderBook) return null;
  const accounts: PublicKey[] = [];
  const scheme: number[][] = [];
  const idsToSettle: number[] = [];

  const isOrdersProvided = ordersIds.length > 0;

  const allOrders = type === ORDER_TYPE.BUY ? orderBook.bids : orderBook.asks;

  const orders = isOrdersProvided
    ? ordersIds.map((id) => allOrders[id])
    : allOrders;
  for (const order of orders) {
    if (scheme.length >= limit) {
      break;
    }
    if (
      order.size > 0 &&
      (isOrdersProvided ||
        order.isFilled === 1 ||
        order.expiryTimestamp < Date.now() / 1000)
    ) {
      const orderUser = order.user;

      const orderIndex = allOrders.findIndex(
        (o) => o.id === order.id && o.size > 0,
      );

      if (idsToSettle.includes(orderIndex)) {
        continue;
      }

      idsToSettle.push(orderIndex);

      const positionState = getUserPositionStateAccount(orderUser, marketId);
      let ata = getAssociatedTokenAddressSync(
        token,
        orderUser,
        true, // Set to false when checking existing accounts
      );
      const userState = getUserStateAccount(orderUser);

      // Check if account exists
      const ataInfo = await retryWithDelay(() =>
        connection.getAccountInfo(ata),
      );
      if (!ataInfo) {
        ata = getAssociatedTokenAddressSync(
          token,
          userState,
          true, // Set to false when checking existing accounts
        );
      }

      const userStateAccount = await retryWithDelay(() =>
        program.account.userState.fetch(userState),
      );
      let referrerState: PublicKey | null = null;

      if (userStateAccount.referrer) {
        referrerState = getUserStateAccount(userStateAccount.referrer);
      }

      if (!accounts.includes(positionState)) {
        accounts.push(positionState);
      }
      if (!accounts.includes(ata)) {
        accounts.push(ata);
      }
      if (!accounts.includes(userState)) {
        accounts.push(userState);
      }

      if (referrerState) {
        if (!accounts.includes(referrerState)) {
          accounts.push(referrerState);
        }
        scheme.push([
          orderIndex,
          accounts.indexOf(positionState),
          accounts.indexOf(ata),
          accounts.indexOf(userState),
          accounts.indexOf(referrerState),
        ]);
      } else {
        scheme.push([
          orderIndex,
          accounts.indexOf(positionState),
          accounts.indexOf(ata),
          accounts.indexOf(userState),
        ]);
      }
    }
  }
  return { accounts, scheme, idsToSettle };
};

export const getTotalSizeAtHeadPrice = (
  orderbook: OrderbookState,
  side: ORDER_TYPE,
  outcome: ORDER_OUTCOME,
  price: number,
) => {
  if (!orderbook) return null;
  const mappedOrders = mapOrderbook(orderbook, outcome, 99999);

  const orders =
    side === ORDER_TYPE.BUY ? mappedOrders.asks : mappedOrders.bids;

  if (orders.length === 0 || orders.every((order) => order.size === 0)) {
    return 0;
  }

  // Sum up all orders at the best price
  const totalSizeAtBestPrice = orders
    .filter((order) =>
      side === ORDER_TYPE.BUY ? order.price <= price : order.price >= price,
    )
    .reduce((total, order) => total + order.size, 0);

  return totalSizeAtBestPrice;
};
